import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  DeviceService,
  DeviceServiceBlockedError,
  DeviceServiceInsecureError,
  DeviceServicePermissionError,
  lastDeviceProbeNotes,
  scanForDeviceService,
  deviceServicePort,
  type DeviceInfo,
} from '../../lib/deviceService/client';
import { useDeviceServiceStatus } from '../../lib/deviceService/status';
import { permissionHelp } from '../../lib/deviceService/permissionHelp';
import { parseLibraryVersion } from '../../lib/deviceService/version';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client';
import { useSupport } from '../support/SupportWidget';
import { ScannerSeatPrompt } from '../devices/ScannerLicense';

interface Scanner {
  man: string; // vendor key passed to the Device Service
  name: string;
}

const SCANNERS: Scanner[] = [
  { man: 'IB', name: 'Integrated Biometrics Kojak' },
  { man: 'IB', name: 'Integrated Biometrics Five-0' },
  { man: 'Thales', name: 'Thales DactyScan 84C' },
  { man: 'Crossmatch', name: 'Crossmatch Guardian' },
  { man: 'Suprema', name: 'Suprema RealScan-G10' },
];

type Status = 'checking' | 'running' | 'blocked' | 'insecure' | 'permission' | 'inactive';

export function DeviceStatus() {
  const { openSupport } = useSupport();
  const [status, setStatus] = useState<Status>('checking');
  const [version, setVersion] = useState<string | null>(null);
  const [bits, setBits] = useState<string | null>(null);
  const [scanners, setScanners] = useState<DeviceInfo[]>([]);
  const [svc, setSvc] = useState<DeviceService | null>(null);
  const [detail, setDetail] = useState<string | null>(null);

  const [selected, setSelected] = useState<Scanner>(SCANNERS[0]!);
  const [libInstalled, setLibInstalled] = useState<string | null>(null);
  const [libAvailable, setLibAvailable] = useState<boolean | null>(null);
  const { report, belowMinimum, minVersion } = useDeviceServiceStatus();
  const qc = useQueryClient();
  const port = deviceServicePort();
  /** Port scan state: which port is being tried, and what came of it. */
  const [scan, setScan] = useState<{ port: number; i: number; total: number } | null>(null);
  const [scanResult, setScanResult] = useState<{ port: number; saved: boolean } | 'none' | null>(null);


  const check = useCallback(async () => {
    setStatus('checking');
    setDetail(null);
    try {
      const s = await DeviceService.connect();
      setSvc(s);
      const [v, b, list] = await Promise.all([s.version(), s.bits().catch(() => null), s.listScanners().catch(() => [])]);
      setVersion(v);
      report(v); // feed the app-wide update check

      setBits(b);
      setScanners(list);
      setStatus('running');
    } catch (e) {
      setStatus(
        e instanceof DeviceServicePermissionError ? 'permission'
          : e instanceof DeviceServiceInsecureError ? 'insecure'
            : e instanceof DeviceServiceBlockedError ? 'blocked'
            : 'inactive',
      );
      // Keep the specific reason discovery gave (which port answered, and whether /version rejected the
      // call or the browser blocked the preflight) — the status word alone can't tell them apart.
      setDetail(e instanceof Error ? e.message : null);
      const notes = lastDeviceProbeNotes();
      if (notes.length) console.warn('[DeviceService] discovery notes:\n' + notes.join('\n'));
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  /**
   * Find a Device Service that isn't on the configured port, then remember where it was.
   *
   * Behind a button rather than run on every connect: the old automatic sweep cost sixty requests each time
   * for the handful of workstations that are not on 8080. Saving the answer to the profile means the customer
   * is asked this once per person, not once per session.
   */
  const findService = useCallback(async () => {
    setScanResult(null);
    setScan({ port: 0, i: 0, total: 0 });
    try {
      const port = await scanForDeviceService((port, i, total) => setScan({ port, i, total }));
      if (port === null) { setScanResult('none'); return; }
      let saved = false;
      try {
        await api.patch('/profile', { deviceServicePort: port });
        qc.invalidateQueries({ queryKey: ['profile'] });
        saved = true;
      } catch {
        // Found but not stored — still usable this session, and saying so beats implying it is permanent.
      }
      setScanResult({ port, saved });
      await check();
    } finally {
      setScan(null);
    }
  }, [check, qc]);

  // When connected, check the selected scanner's device library.
  useEffect(() => {
    if (status !== 'running' || !svc) return;
    setLibInstalled(null);
    setLibAvailable(null);
    // Sanitise: the endpoint answers with "Error checking IB installation (Was the correct vendor
    // selected?)" when the library isn't there, which was rendered verbatim as the installed version.
    void svc.libraryInstalled(selected.man).then((raw) => setLibInstalled(parseLibraryVersion(raw)));
    void svc.libraryAvailable(selected.man).then(setLibAvailable);
  }, [status, svc, selected]);

  const running = status === 'running';

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Device status</h1>
          <div className="sub">Check that the local Device Service and your scanner are ready to capture.</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => void check()} disabled={status === 'checking'}>
          {status === 'checking' ? 'Checking…' : 'Re-check'}
        </button>
      </div>

      {/* Service status */}
      <div className={`ds-status ds-${status}`}>
        <span className="ds-dot" />
        <div>
          <strong>
            {status === 'checking' ? 'Checking Device Service…'
              : running ? 'Device Service is running'
              : status === 'permission' ? 'Blocked by this browser’s local network permission'
              : status === 'blocked' ? 'Device Service detected, but blocked by the browser'
              : status === 'insecure' ? 'Device Service not reachable'
              : 'Device Service is inactive'}
          </strong>
          <div className="ds-sub">
            {running
              ? `Version ${version}${bits ? ` · ${bits}-bit` : ''} · ${scanners.length} scanner(s) detected`
              : status === 'permission'
                /* The control this browser actually shows — Chromium calls it something else entirely. */
                ? `Set “${permissionHelp(navigator.userAgent, 'denied').controlName}” to Allow for this site in your browser’s site permissions, then Re-check.`
                : `Start the EFTCreator Device Service on this workstation (expected on port ${port}), then Re-check.`}
          </div>
        </div>
      </div>

      {/*
        * The escape hatch for a workstation that is not on the default port. Only offered when the service was
        * not found — and not when the BROWSER is the thing refusing, where scanning more ports would just be
        * twenty more refusals and a misleading conclusion.
        */}
      {!running && status !== 'checking' && status !== 'permission' && (
        <div className="device-help">
          <p style={{ marginTop: 0 }}>
            EFTCreator expects the Device Service on <strong>port {port}</strong>. If yours was set up on a
            different port, search for it — the port is then saved to your profile so this is a one-time step.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="btn btn-primary btn-sm" disabled={scan !== null} onClick={() => void findService()}>
              {scan ? `Searching port ${scan.port || '…'}…` : 'Search for the Device Service'}
            </button>
            {scan && scan.total > 0 && (
              <span className="field-help">{scan.i + 1} of {scan.total}</span>
            )}
          </div>
          {scanResult === 'none' && (
            <p className="cart-error" style={{ marginBottom: 0 }}>
              No Device Service answered on any of the usual ports. Start it from the Windows Start menu, or set
              the port by hand on <Link to="/app/profile" className="track-link">My Profile</Link>.
            </p>
          )}
          {scanResult && scanResult !== 'none' && (
            <p className="banner-ok" style={{ marginBottom: 0 }}>
              Found it on port <strong>{scanResult.port}</strong>.{' '}
              {scanResult.saved
                ? 'Saved to your profile — it will be used automatically from now on.'
                : <>Could not save it to your profile; set it on <Link to="/app/profile" className="track-link">My Profile</Link> so it sticks.</>}
            </p>
          )}
        </div>
      )}

      {detail && !running && (
        <div className="device-help">
          <strong>Details:</strong> {detail}
        </div>
      )}

      {status === 'insecure' && (
        <div className="device-help">
          Use <strong>Chrome or Edge</strong> to reach the local Device Service from this site. Safari and Firefox
          block calls from an HTTPS page to <span className="mono">http://localhost</span>.
        </div>
      )}

      {belowMinimum && (
        <div className="banner-warn" style={{ marginTop: 12 }}>
          <strong>This Device Service is too old to capture.</strong> v{minVersion} or newer is required.
          Update it from the Downloads page and restart it — captures are refused until then.
        </div>
      )}

      {running && (
        <div className="stats" style={{ marginTop: 4 }}>
          <div className="stat"><div className="stat-label">Service version</div><div className="stat-value">{version}</div></div>
          <div className="stat"><div className="stat-label">Architecture</div><div className="stat-value">{bits ? `${bits}-bit` : '—'}</div></div>
          <div className="stat"><div className="stat-label">Scanners detected</div><div className="stat-value">{scanners.length}</div></div>
        </div>
      )}

      {running && scanners.length > 0 && (
        <>
          <div className="section-title">Connected devices</div>
          <div className="panel">
            <ul className="team">
              {scanners.map((d) => (
                <li key={d.deviceId}>
                  <span className="who"><span className="avatar">◉</span>{d.deviceName} <span style={{ color: 'var(--text-faint)' }}>· {d.vendorName ?? '—'}</span></span>
                  <span className="role-pill mono">{d.deviceSerial}</span>
                </li>
              ))}
            </ul>
            {/* This is where someone comes to answer "is my scanner ready?", so an unlicensed scanner with
                a seat going spare is worth resolving here rather than at the next capture attempt. */}
            {scanners.map((d) => <ScannerSeatPrompt key={d.deviceId} serial={d.deviceSerial} />)}
          </div>
        </>
      )}

      {/* Scanner library check */}
      <div className="section-title">Scanner device library</div>
      <div className="panel">
        <div className="ds-scanner-row">
          <label className="field" style={{ maxWidth: 320 }}>
            <span className="field-label">Select your scanner</span>
            <select className="select" value={selected.name} onChange={(e) => setSelected(SCANNERS.find((s) => s.name === e.target.value) ?? SCANNERS[0]!)}>
              {SCANNERS.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
          </label>
        </div>
        <div className="review-grid" style={{ marginTop: 16 }}>
          <div className="review-row"><span>Manufacturer</span><strong>{selected.man}</strong></div>
          <div className="review-row">
            <span>Device library installed</span>
            <strong style={{ color: libInstalled ? 'var(--accent-bright)' : 'var(--amber)' }}>
              {running ? (libInstalled ?? 'Not found') : '—'}
            </strong>
          </div>
          <div className="review-row">
            <span>Newer library available</span>
            <strong>{running ? (libAvailable === null ? '…' : libAvailable ? 'Yes' : 'Up to date') : '—'}</strong>
          </div>
        </div>
        <p style={{ marginTop: 16 }}>
          <Link className="track-link" to="/app/downloads">
            Download / update the {selected.man} device library →
          </Link>
        </p>
      </div>

      {/* Troubleshooting */}
      <div className="section-title">Troubleshooting</div>
      <div className="panel ds-tips">
        <ul>
          <li>Make sure the <strong>EFTCreator Device Service</strong> is running on this workstation (it starts with Windows by default).</li>
          <li>Use <strong>Chrome or Microsoft Edge</strong> — Safari and Firefox block access to the local device service.</li>
          <li>Plug the scanner directly into a USB port (avoid hubs) and confirm it appears under <em>Connected devices</em> above.</li>
          <li>If your scanner isn’t detected, install/update its <strong>device library</strong> using the link above, then Re-check.</li>
          <li>Still stuck? <button type="button" className="linklike" onClick={openSupport}>Ask our support assistant or open a ticket</button>.</li>
        </ul>
      </div>
    </div>
  );
}
